
//* Components Imports
import { Form, Alert, Badge } from 'react-bootstrap';

//* Libraries imports
import { motion, AnimatePresence } from 'framer-motion';

//* Icons imports
import { MdCloudUpload, MdClose } from 'react-icons/md';

//* Components imports
import Icon from "../../../../../../components/common/Icon";

//* Animations imports
import { formItemVariants } from '../animations';

type FileUploadProps = {
  uploadedFiles: File[];
  existingFiles?: Array<{id: string, name: string, path: string}>;
  uploadError: string | null;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  onRemoveExistingFile?: (fileId: string) => void;
  isEditMode?: boolean;
};

export function FileUpload({ 
  uploadedFiles, 
  existingFiles = [],
  uploadError, 
  onFileChange, 
  onRemoveFile,
  onRemoveExistingFile,
  isEditMode = false
}: FileUploadProps) {
  return (
    <motion.div variants={formItemVariants} custom={9}>
      <Form.Group className="mb-4">
        <Form.Label>
          Anexos (máximo 10 arquivos)
          {isEditMode && existingFiles.length > 0 && (
            <small className="text-muted ms-2">
              - {existingFiles.length + uploadedFiles.length} arquivo(s) no
              total
            </small>
          )}
        </Form.Label>
        <div className="file-upload-container">
          <motion.label
            htmlFor="file-upload"
            className="file-upload-label"
            whileHover={{ scale: 1.02, backgroundColor: "#e9ecef" }}            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            <Icon icon={MdCloudUpload} size={24} className="me-2" />
            <span>Selecionar arquivos</span>
            <input
              id="file-upload"
              type="file"
              multiple
              className="d-none"
              accept=".jpg,.jpeg,.png"
              title="Selecionar arquivos para anexar"
              aria-label="Selecionar arquivos para anexar"
              onChange={onFileChange}
            />
          </motion.label>
        </div>
        {uploadError && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert variant="danger" className="mt-2">
              {uploadError}
            </Alert>
          </motion.div>
        )}{" "}
        {(uploadedFiles.length > 0 || existingFiles.length > 0) && (
          <div className="uploaded-files-container mt-3">
            {/* Files exists */}
            {isEditMode && existingFiles.length > 0 && (
              <>
                <h6 className="text-muted mb-2">Arquivos existentes:</h6>
                <AnimatePresence initial={false}>
                  {existingFiles.map((file) => (
                    <motion.div
                      key={`existing-file-${file.id}`}
                      className="uploaded-file-item existing-file"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{
                        duration: 0.3,
                        type: "spring",
                        stiffness: 200,
                      }}
                    >
                      <span className="file-name text-truncate me-2">
                        {file.name}
                      </span>
                      <Badge bg="info" className="me-2">
                        Existente
                      </Badge>{" "}
                      {onRemoveExistingFile && (
                        <motion.button
                          type="button"
                          className="btn btn-link p-0 text-danger"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          transition={{ duration: 0.2 }}                          title={`Remover arquivo ${file.name}`}
                          aria-label={`Remover arquivo ${file.name}`}
                          onClick={() => onRemoveExistingFile(file.id)}
                        >
                          <Icon icon={MdClose} size={18} />
                        </motion.button>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </>
            )}

            {/* New Files */}
            {uploadedFiles.length > 0 && (
              <>
                {isEditMode && existingFiles.length > 0 && (
                  <h6 className="text-muted mb-2 mt-3">Novos arquivos:</h6>
                )}
                <AnimatePresence initial={false}>
                  {uploadedFiles.map((file, index) => (
                    <motion.div
                      key={`file-${index}`}
                      className="uploaded-file-item"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{
                        duration: 0.3,
                        type: "spring",
                        stiffness: 200,
                      }}
                    >
                      <span className="file-name text-truncate me-2">
                        {file.name}
                      </span>
                      <Badge bg="secondary" className="me-2">
                        {(file.size / 1024).toFixed(0)} KB
                      </Badge>
                      <motion.button
                        type="button"
                        className="btn btn-link p-0 text-danger"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2 }}                        title={`Remover arquivo ${file.name}`}
                        aria-label={`Remover arquivo ${file.name}`}
                        onClick={() => onRemoveFile(index)}
                      >
                        <Icon icon={MdClose} size={18} />
                      </motion.button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </>
            )}
          </div>
        )}
      </Form.Group>
    </motion.div>
  );
}
